"use client"

import { useState, useMemo } from "react"
import { Plus, Trash2, RotateCcw } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"
import { eurosEn, numeroEn } from "./formato"

interface Producto {
  id: number
  nombre: string
  pesoBruto: number
  pesoNeto: number
  precioKg: number
}

const defaultProductos: Record<Idioma, Producto[]> = {
  es: [
    { id: 1, nombre: "Merluza fresca", pesoBruto: 1000, pesoNeto: 600, precioKg: 12 },
    { id: 2, nombre: "Lechuga iceberg", pesoBruto: 500, pesoNeto: 400, precioKg: 2 },
    { id: 3, nombre: "Patatas", pesoBruto: 2000, pesoNeto: 1700, precioKg: 1.2 },
  ],
  en: [
    { id: 1, nombre: "Fresh hake", pesoBruto: 1000, pesoNeto: 600, precioKg: 12 },
    { id: 2, nombre: "Iceberg lettuce", pesoBruto: 500, pesoNeto: 400, precioKg: 2 },
    { id: 3, nombre: "Potatoes", pesoBruto: 2000, pesoNeto: 1700, precioKg: 1.2 },
  ],
}

const T = {
  es: {
    productos: "Productos",
    limpiar: "Limpiar",
    nombreProducto: "Nombre del producto",
    pesoBruto: "Peso bruto (g)",
    pesoNeto: "Peso neto (g)",
    precioKg: "Precio/kg (€)",
    anadir: "Añadir producto",
    tipicas: "📊 Mermas típicas",
    pescado: "Pescado:",
    carne: "Carne:",
    verdura: "Verdura:",
    fruta: "Fruta:",
    marisco: "Marisco:",
    analisis: "Análisis de Mermas",
    mediaPonderada: "Merma media ponderada",
    brutoNeto: (bruto: string, neto: string) => `${bruto} kg bruto → ${neto} kg neto`,
    desglose: "Desglose por producto",
    sinNombre: "Sin nombre",
    merma: (pct: string) => `${pct}% merma`,
    precioOriginal: "Precio original:",
    precioReal: "Precio real (neto):",
    sobrecoste: "Sobrecoste:",
    consejos: "💡 Consejos",
    listaConsejos: [
      "• Registra mermas semanalmente para detectar desviaciones",
      "• Compara con proveedores: a veces lo barato sale caro",
      "• Aprovecha recortes: caldos, fondos, personal",
      "• Forma al equipo en técnicas de despiece",
    ],
  },
  en: {
    productos: "Products",
    limpiar: "Reset",
    nombreProducto: "Product name",
    pesoBruto: "Gross weight (g)",
    pesoNeto: "Usable weight (g)",
    precioKg: "Price/kg (€)",
    anadir: "Add product",
    tipicas: "📊 Typical waste",
    pescado: "Fish:",
    carne: "Meat:",
    verdura: "Vegetables:",
    fruta: "Fruit:",
    marisco: "Shellfish:",
    analisis: "Waste analysis",
    mediaPonderada: "Weighted average waste",
    brutoNeto: (bruto: string, neto: string) => `${bruto} kg gross → ${neto} kg usable`,
    desglose: "Breakdown by product",
    sinNombre: "Unnamed",
    merma: (pct: string) => `${pct}% waste`,
    precioOriginal: "Purchase price:",
    precioReal: "True net price:",
    sobrecoste: "Extra cost:",
    consejos: "💡 Tips",
    listaConsejos: [
      "• Record waste every week to spot deviations early",
      "• Compare suppliers: the cheapest isn't always the cheapest once trimmed",
      "• Use the trimmings: stocks, bases and staff meals",
      "• Train your team in butchery and filleting techniques",
    ],
  },
}

const benchmarks: Record<string, { min: number; max: number }> = {
  pescado: { min: 35, max: 50 },
  carne: { min: 15, max: 30 },
  verdura: { min: 10, max: 25 },
  fruta: { min: 15, max: 30 },
  marisco: { min: 40, max: 60 },
}

let nextId = 100

export default function MermasCalculator({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const num = (n: number, d: number) => (idioma === "en" ? numeroEn(n, d) : n.toFixed(d))
  const porKg = (n: number) => (idioma === "en" ? `${eurosEn(n)}/kg` : `${n.toFixed(2)}€/kg`)
  const [productos, setProductos] = useState<Producto[]>(defaultProductos[idioma])

  const results = useMemo(() => {
    return productos.map((p) => {
      const mermaPct = p.pesoBruto > 0 ? ((p.pesoBruto - p.pesoNeto) / p.pesoBruto) * 100 : 0
      const precioNetoReal = p.pesoNeto > 0 ? (p.pesoBruto * p.precioKg) / (p.pesoNeto / 1000) / 1000 : 0
      const costeOriginal = (p.pesoBruto / 1000) * p.precioKg
      const sobrecoste = precioNetoReal > 0 ? precioNetoReal - p.precioKg : 0
      const sobrecostePct = p.precioKg > 0 ? (sobrecoste / p.precioKg) * 100 : 0

      return {
        ...p,
        mermaPct,
        precioNetoReal,
        costeOriginal,
        sobrecoste,
        sobrecostePct,
      }
    })
  }, [productos])

  const totals = useMemo(() => {
    const totalBruto = productos.reduce((sum, p) => sum + p.pesoBruto, 0)
    const totalNeto = productos.reduce((sum, p) => sum + p.pesoNeto, 0)
    const totalCoste = productos.reduce((sum, p) => sum + (p.pesoBruto / 1000) * p.precioKg, 0)
    const mermaTotalPct = totalBruto > 0 ? ((totalBruto - totalNeto) / totalBruto) * 100 : 0

    return { totalBruto, totalNeto, totalCoste, mermaTotalPct }
  }, [productos])

  const addProducto = () => {
    setProductos([
      ...productos,
      { id: nextId++, nombre: "", pesoBruto: 0, pesoNeto: 0, precioKg: 0 },
    ])
  }

  const removeProducto = (id: number) => {
    if (productos.length > 1) {
      setProductos(productos.filter((p) => p.id !== id))
    }
  }

  const updateProducto = (id: number, field: keyof Producto, value: string | number) => {
    setProductos(
      productos.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    )
  }

  const reset = () => {
    setProductos(defaultProductos[idioma])
  }

  const getMermaColor = (pct: number) => {
    if (pct <= 15) return "text-emerald-600"
    if (pct <= 30) return "text-amber-600"
    return "text-red-600"
  }

  const getMermaBg = (pct: number) => {
    if (pct <= 15) return "bg-emerald-50"
    if (pct <= 30) return "bg-amber-50"
    return "bg-red-50"
  }

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-200">
        {/* Inputs */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900">{t.productos}</h2>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t.limpiar}
            </button>
          </div>

          <div className="space-y-4">
            {productos.map((p) => (
              <div
                key={p.id}
                className="bg-zinc-50 rounded-xl p-4 space-y-3"
              >
                <div className="flex items-start gap-2">
                  <input
                    type="text"
                    value={p.nombre}
                    onChange={(e) => updateProducto(p.id, "nombre", e.target.value)}
                    placeholder={t.nombreProducto}
                    className="flex-1 px-3 py-2 rounded-xl border border-zinc-200 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent bg-white"
                  />
                  <button
                    onClick={() => removeProducto(p.id)}
                    className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">{t.pesoBruto}</label>
                    <input
                      type="number"
                      value={p.pesoBruto || ""}
                      onChange={(e) =>
                        updateProducto(p.id, "pesoBruto", parseFloat(e.target.value) || 0)
                      }
                      placeholder="1000"
                      min="0"
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">{t.pesoNeto}</label>
                    <input
                      type="number"
                      value={p.pesoNeto || ""}
                      onChange={(e) =>
                        updateProducto(p.id, "pesoNeto", parseFloat(e.target.value) || 0)
                      }
                      placeholder="700"
                      min="0"
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">{t.precioKg}</label>
                    <input
                      type="number"
                      value={p.precioKg || ""}
                      onChange={(e) =>
                        updateProducto(p.id, "precioKg", parseFloat(e.target.value) || 0)
                      }
                      placeholder="12"
                      min="0"
                      step="0.1"
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent bg-white"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={addProducto}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-lime-600 hover:text-lime-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t.anadir}
          </button>

          {/* Benchmarks */}
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-sky-800 mb-2">{t.tipicas}</h3>
            <div className="grid grid-cols-2 gap-2 text-sm text-sky-700">
              <span>{t.pescado}</span><span>35-50%</span>
              <span>{t.carne}</span><span>15-30%</span>
              <span>{t.verdura}</span><span>10-25%</span>
              <span>{t.fruta}</span><span>15-30%</span>
              <span>{t.marisco}</span><span>40-60%</span>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="p-6 md:p-8 bg-zinc-50 space-y-6">
          <h2 className="text-lg font-semibold text-zinc-900">{t.analisis}</h2>

          {/* Total KPI */}
          <div className={`rounded-2xl p-6 ${getMermaBg(totals.mermaTotalPct)}`}>
            <p className="text-sm font-medium text-zinc-600 mb-1">{t.mediaPonderada}</p>
            <p className={`text-5xl font-bold ${getMermaColor(totals.mermaTotalPct)}`}>
              {num(totals.mermaTotalPct, 1)}%
            </p>
            <p className="text-sm text-zinc-500 mt-2">
              {t.brutoNeto(num(totals.totalBruto / 1000, 2), num(totals.totalNeto / 1000, 2))}
            </p>
          </div>

          {/* Per product results */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-zinc-700">{t.desglose}</h3>
            {results.map((r) => (
              <div
                key={r.id}
                className="bg-white rounded-xl border border-zinc-200 p-4"
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="font-medium text-zinc-900">
                    {r.nombre || t.sinNombre}
                  </span>
                  <span className={`text-lg font-bold ${getMermaColor(r.mermaPct)}`}>
                    {t.merma(num(r.mermaPct, 1))}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <span className="text-zinc-500">{t.precioOriginal}</span>
                  <span className="text-zinc-900">{porKg(r.precioKg)}</span>
                  <span className="text-zinc-500">{t.precioReal}</span>
                  <span className="font-medium text-red-600">{porKg(r.precioNetoReal)}</span>
                  <span className="text-zinc-500">{t.sobrecoste}</span>
                  <span className="text-amber-600">+{num(r.sobrecostePct, 1)}%</span>
                </div>
              </div>
            ))}
          </div>

          {/* Tips */}
          <div className="bg-lime-50 border border-lime-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-lime-800 mb-2">{t.consejos}</h3>
            <ul className="text-sm text-lime-700 space-y-1">
              {t.listaConsejos.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
